import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAppSettings } from '../context/SettingsContext';
import { getActiveDudaAccount } from '../settings/dudaAccounts';
import { downloadDudaAnalyticsCache } from '../services/dudaAnalyticsDownload';
import type { DudaChartMetric } from '../services/dudaApi';
import { parseDudaActivitiesBreakdown, parseDudaSeries } from '../services/dudaApi';
import {
  clearDudaAnalyticsCache,
  exportDudaSqliteUint8Array,
  importDudaSqliteArrayBuffer,
  loadDudaAnalyticsCache,
  saveDudaAnalyticsCache,
  type DudaAnalyticsCacheV1,
} from '../services/dudaAnalyticsStorage';
import { datasetLabel, flattenDudaAnalyticsCache, type AnalyticsDataset } from '../services/dudaAnalyticsFlatten';
import type { DudaDateGranularity } from '../services/dudaApi';

function fmtIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtNum(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function fmtWhenShort(iso?: string): string {
  if (!iso?.trim()) {
    return '—';
  }
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'PPp') : iso;
  } catch {
    return iso;
  }
}

const DATE_PRESETS = ['7d', '28d', '90d', '12m'] as const;

function isDatePreset(p: string): p is (typeof DATE_PRESETS)[number] {
  return (DATE_PRESETS as readonly string[]).includes(p);
}

function rangeForPreset(preset: string): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  if (preset === '7d') {
    from.setDate(from.getDate() - 7);
  } else if (preset === '28d') {
    from.setDate(from.getDate() - 28);
  } else if (preset === '90d') {
    from.setDate(from.getDate() - 90);
  } else if (preset === '12m') {
    from.setFullYear(from.getFullYear() - 1);
  } else {
    from.setDate(from.getDate() - 28);
  }
  return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
}

function aggregateTrafficSeries(
  cache: DudaAnalyticsCacheV1,
  siteFilter: string,
  metric: DudaChartMetric
): { label: string; value: number }[] {
  const map = new Map<string, number>();
  const sites = siteFilter ? [siteFilter] : Object.keys(cache.sites);
  for (const sn of sites) {
    const blob = cache.sites[sn]?.trafficSummary;
    if (blob == null) {
      continue;
    }
    for (const p of parseDudaSeries(blob, metric)) {
      map.set(p.label, (map.get(p.label) ?? 0) + p.value);
    }
  }
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function KpiCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: 'divider',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}
    >
      <CardContent sx={{ py: 2.25, px: 2.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}
        >
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.75, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const DudaAnalyticsCachePage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useAppSettings();
  const dudaAccount = useMemo(() => getActiveDudaAccount(settings), [settings]);
  const accountId = dudaAccount.id;
  const { apiUser, apiPassword } = dudaAccount;
  const credsOk = apiUser.trim() && apiPassword.trim();

  const [datePreset, setDatePreset] = useState<string>(DATE_PRESETS[1]);
  const [from, setFrom] = useState(() => rangeForPreset(DATE_PRESETS[1]).from);
  const [to, setTo] = useState(() => rangeForPreset(DATE_PRESETS[1]).to);
  const [granularity, setGranularity] = useState<DudaDateGranularity>('DAYS');

  const [cache, setCache] = useState<DudaAnalyticsCacheV1 | null>(null);
  const [dbLoading, setDbLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [saveHint, setSaveHint] = useState<string | null>(null);

  const [filterSite, setFilterSite] = useState('');
  const [chartMetric, setChartMetric] = useState<DudaChartMetric>('visitors');
  const [search, setSearch] = useState('');
  const [filterDataset, setFilterDataset] = useState<AnalyticsDataset | ''>('');

  const syncSeq = useRef(0);

  const flatRows = useMemo(() => (cache ? flattenDudaAnalyticsCache(cache) : []), [cache]);

  const siteOptions = useMemo(() => {
    if (!cache) {
      return [];
    }
    return Object.keys(cache.sites).sort((a, b) => a.localeCompare(b));
  }, [cache]);

  const trendData = useMemo(() => {
    if (!cache) {
      return [];
    }
    return aggregateTrafficSeries(cache, filterSite, chartMetric);
  }, [cache, filterSite, chartMetric]);

  const kpis = useMemo(() => {
    if (!cache) {
      return { visitors: 0, visits: 0, pageViews: 0, engagements: 0 };
    }
    const v = aggregateTrafficSeries(cache, filterSite, 'visitors').reduce((s, p) => s + p.value, 0);
    const vi = aggregateTrafficSeries(cache, filterSite, 'visits').reduce((s, p) => s + p.value, 0);
    const pv = aggregateTrafficSeries(cache, filterSite, 'pageviews').reduce((s, p) => s + p.value, 0);
    let eng = 0;
    const sites = filterSite ? [filterSite] : Object.keys(cache.sites);
    for (const sn of sites) {
      const b = parseDudaActivitiesBreakdown(cache.sites[sn]?.activities);
      eng += b.reduce((s, x) => s + x.value, 0);
    }
    return { visitors: v, visits: vi, pageViews: pv, engagements: eng };
  }, [cache, filterSite]);

  const geoBars = useMemo(() => {
    const rows = flatRows.filter((r) => r.dataset === 'traffic_geo' && (!filterSite || r.siteName === filterSite));
    const sorted = [...rows].sort((a, b) => (b.visitors ?? 0) - (a.visitors ?? 0)).slice(0, 10);
    return sorted.map((r) => ({
      name: (r.dimensionLabel || '—').slice(0, 42),
      visitors: r.visitors ?? 0,
    }));
  }, [flatRows, filterSite]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flatRows.filter((r) => {
      if (filterSite && r.siteName !== filterSite) {
        return false;
      }
      if (filterDataset && r.dataset !== filterDataset) {
        return false;
      }
      if (!q) {
        return true;
      }
      const hay = [r.siteName, r.period, r.dimensionLabel, r.dataset]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [flatRows, filterSite, filterDataset, search]);

  const apiIssueCount = useMemo(() => {
    if (!cache) {
      return 0;
    }
    let n = 0;
    for (const s of Object.values(cache.sites)) {
      if (s.trafficSummaryError) {
        n += 1;
      }
      if (s.trafficByGeoError) {
        n += 1;
      }
      if (s.trafficBySystemError) {
        n += 1;
      }
      if (s.activitiesError) {
        n += 1;
      }
    }
    return n;
  }, [cache]);

  const runRemoteSync = useCallback(async () => {
    if (!credsOk) {
      return;
    }
    const seq = ++syncSeq.current;
    setSyncing(true);
    setSyncError(null);
    setSaveHint(null);
    try {
      const bundle = await downloadDudaAnalyticsCache({
        apiUser,
        apiPassword,
        from,
        to,
        dateGranularity: granularity,
        siteConcurrency: 2,
        onProgress: () => {},
      });
      if (seq !== syncSeq.current) {
        return;
      }
      const saved = await saveDudaAnalyticsCache(accountId, bundle);
      if (!saved.ok) {
        setSaveHint(saved.error);
      }
      setCache(bundle);
    } catch (e) {
      if (seq === syncSeq.current) {
        setSyncError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (seq === syncSeq.current) {
        setSyncing(false);
      }
    }
  }, [accountId, apiPassword, apiUser, credsOk, from, granularity, to]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDbLoading(true);
      try {
        const loaded = await loadDudaAnalyticsCache(accountId);
        if (!cancelled) {
          setCache(loaded);
        }
      } catch {
        if (!cancelled) {
          setCache(null);
        }
      } finally {
        if (!cancelled) {
          setDbLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (dbLoading || !credsOk) {
      return;
    }
    const t = window.setTimeout(() => {
      void runRemoteSync();
    }, 400);
    return () => window.clearTimeout(t);
  }, [dbLoading, credsOk, accountId, from, to, granularity, runRemoteSync]);

  const handlePresetChange = (_: React.SyntheticEvent, value: string | null) => {
    if (!value || !isDatePreset(value)) {
      return;
    }
    setDatePreset(value);
    const r = rangeForPreset(value);
    setFrom(r.from);
    setTo(r.to);
  };

  const handleExportBackup = async () => {
    if (!cache) {
      return;
    }
    try {
      const u8 = await exportDudaSqliteUint8Array(accountId);
      const blob = new Blob([u8], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `duda-analytics-backup-${cache.downloadedAt.slice(0, 10)}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  const handleImportBackup: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    void (async () => {
      try {
        const buf = await file.arrayBuffer();
        await importDudaSqliteArrayBuffer(accountId, buf);
        setCache(await loadDudaAnalyticsCache(accountId));
        setSaveHint(null);
        setSyncError(null);
      } catch (err) {
        setSyncError(err instanceof Error ? err.message : String(err));
      }
    })();
    e.target.value = '';
  };

  const handleClearLocal = async () => {
    await clearDudaAnalyticsCache(accountId);
    setCache(null);
    setSaveHint(null);
    setSyncError(null);
    if (credsOk) {
      void runRemoteSync();
    }
  };

  const rangeLabel = `${from} → ${to}`;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: '#f4f6f9',
        pb: 4,
        mx: -3,
        mt: -3,
        px: { xs: 2, sm: 3 },
        pt: 3,
      }}
    >
      {syncing && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 64,
            left: { xs: 0, md: 272 },
            right: 0,
            zIndex: 1100,
            height: 3,
            '& .MuiLinearProgress-bar': { bgcolor: '#1a73e8' },
          }}
        />
      )}

      <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h5" fontWeight={800} sx={{ color: '#202124', letterSpacing: '-0.02em' }}>
                Analytics
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  px: 1.25,
                  py: 0.35,
                  borderRadius: 10,
                  bgcolor: 'rgba(26,115,232,0.1)',
                  color: '#1a73e8',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                {dudaAccount.label}
              </Paper>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 560 }}>
              Traffic and engagement across your Duda sites. Data refreshes automatically for the range below.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {cache ? `Updated ${fmtWhenShort(cache.downloadedAt)}` : syncing ? 'Fetching…' : ''}
            </Typography>
            <Tooltip title="Refresh data">
              <span>
                <IconButton
                  onClick={() => void runRemoteSync()}
                  disabled={!credsOk || syncing}
                  sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}
                >
                  {syncing ? <CircularProgress size={22} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" onClick={() => navigate('/duda')} sx={{ bgcolor: '#fff' }}>
              All sites
            </Button>
          </Stack>
        </Stack>

        {!credsOk && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Add Duda API credentials in API settings to load analytics.
          </Alert>
        )}

        {(syncError || saveHint) && (
          <Alert severity={saveHint && !syncError ? 'warning' : 'error'} sx={{ mb: 2, borderRadius: 2 }} onClose={() => { setSyncError(null); setSaveHint(null); }}>
            {syncError || saveHint}
          </Alert>
        )}

        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(60,64,67,0.08)' }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ minWidth: 100 }}>
                Date range
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={isDatePreset(datePreset) ? datePreset : false}
                onChange={handlePresetChange}
                sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1.75, textTransform: 'none', fontWeight: 600 } }}
              >
                <ToggleButton value="7d">Last 7 days</ToggleButton>
                <ToggleButton value="28d">Last 28 days</ToggleButton>
                <ToggleButton value="90d">Last 90 days</ToggleButton>
                <ToggleButton value="12m">Last 12 months</ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <TextField
                  type="date"
                  size="small"
                  label="Start"
                  value={from}
                  onChange={(e) => {
                    setDatePreset('custom');
                    setFrom(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="End"
                  value={to}
                  onChange={(e) => {
                    setDatePreset('custom');
                    setTo(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
              </Stack>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="gran">Detail</InputLabel>
                <Select
                  labelId="gran"
                  label="Detail"
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value as DudaDateGranularity)}
                >
                  <MenuItem value="DAYS">By day</MenuItem>
                  <MenuItem value="WEEKS">By week</MenuItem>
                  <MenuItem value="MONTHS">By month</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Reporting range: {rangeLabel}
            </Typography>
          </CardContent>
        </Card>

        {dbLoading && !cache && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!cache && !dbLoading && !syncing && credsOk && syncError && (
          <Card sx={{ borderRadius: 2, p: 4, textAlign: 'center', mb: 2 }}>
            <Typography color="text.secondary">
              Fix the issue above, then use refresh to try again.
            </Typography>
          </Card>
        )}

        {cache && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <KpiCard title="Users" value={fmtNum(kpis.visitors)} hint={filterSite ? 'Selected site' : 'All sites'} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard title="Sessions" value={fmtNum(kpis.visits)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard title="Page views" value={fmtNum(kpis.pageViews)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard title="Engagement actions" value={fmtNum(kpis.engagements)} hint="Forms & clicks" />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Trend
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Metric</InputLabel>
                        <Select
                          value={chartMetric}
                          label="Metric"
                          onChange={(e) => setChartMetric(e.target.value as DudaChartMetric)}
                        >
                          <MenuItem value="visitors">Users</MenuItem>
                          <MenuItem value="visits">Sessions</MenuItem>
                          <MenuItem value="pageviews">Page views</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    {trendData.length > 0 ? (
                      <Box sx={{ width: '100%', height: 320 }}>
                        <ResponsiveContainer>
                          <AreaChart data={trendData}>
                            <defs>
                              <linearGradient id="dudaFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#1a73e8" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="#1a73e8" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eaed" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5f6368' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#5f6368' }} />
                            <RechartsTooltip />
                            <Area type="monotone" dataKey="value" stroke="#1a73e8" strokeWidth={2} fill="url(#dudaFill)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                        No trend data for this range. Try a longer period or check another metric.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                      Users by region
                    </Typography>
                    {geoBars.length > 0 ? (
                      <Box sx={{ width: '100%', height: 320 }}>
                        <ResponsiveContainer>
                          <BarChart data={geoBars} layout="vertical" margin={{ left: 8, right: 16 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eaed" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: '#5f6368' }} />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: '#5f6368' }} />
                            <RechartsTooltip />
                            <Bar dataKey="visitors" fill="#34a853" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" variant="body2" sx={{ py: 4 }}>
                        Geographic breakdown appears when Duda returns location data for this range.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Explore
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                  <TextField
                    size="small"
                    label="Search"
                    placeholder="Site, channel, period…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220, flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Site</InputLabel>
                    <Select value={filterSite} label="Site" onChange={(e) => setFilterSite(e.target.value)}>
                      <MenuItem value="">
                        <em>All properties</em>
                      </MenuItem>
                      {siteOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Breakdown</InputLabel>
                    <Select
                      value={filterDataset}
                      label="Breakdown"
                      onChange={(e) => setFilterDataset(e.target.value as AnalyticsDataset | '')}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="traffic_total">{datasetLabel('traffic_total')}</MenuItem>
                      <MenuItem value="traffic_geo">{datasetLabel('traffic_geo')}</MenuItem>
                      <MenuItem value="traffic_system">{datasetLabel('traffic_system')}</MenuItem>
                      <MenuItem value="activities">{datasetLabel('activities')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Site</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Dimension</TableCell>
                        <TableCell align="right">Users</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Forms</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Typography color="text.secondary">Nothing matches your filters.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredRows.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{r.siteName}</TableCell>
                          <TableCell>{datasetLabel(r.dataset)}</TableCell>
                          <TableCell>{r.period || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{r.dimensionLabel || '—'}</TableCell>
                          <TableCell align="right">{r.visitors != null ? fmtNum(r.visitors) : '—'}</TableCell>
                          <TableCell align="right">{r.visits != null ? fmtNum(r.visits) : '—'}</TableCell>
                          <TableCell align="right">{r.pageViews != null ? fmtNum(r.pageViews) : '—'}</TableCell>
                          <TableCell align="right">{r.formSubmits != null ? fmtNum(r.formSubmits) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Accordion sx={{ mt: 2, borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600} color="text.secondary">
                  Data &amp; troubleshooting
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Backup or reset the copy of analytics stored in this browser. Refresh above re-downloads from Duda.
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Button variant="outlined" size="small" onClick={() => void handleExportBackup()} disabled={!cache}>
                      Download backup
                    </Button>
                    <Button variant="outlined" size="small" component="label">
                      Restore backup
                      <input type="file" accept="*/*" hidden onChange={handleImportBackup} />
                    </Button>
                    <Button variant="outlined" size="small" color="error" onClick={() => void handleClearLocal()}>
                      Clear local copy
                    </Button>
                  </Stack>
                  {apiIssueCount > 0 && (
                    <Alert severity="warning">
                      Some requests returned errors for one or more sites ({apiIssueCount}). Try refreshing or a shorter
                      range.
                    </Alert>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DudaAnalyticsCachePage;
